/**
 * ApplovinIntegrationPage — SDK key form + sync status + recent install feed.
 * Mobile Ad Network ingest partner. GREEN accent only.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useApplovinIntegrationCreate,
	useApplovinIntegrationList,
} from "./hooks";
import type { ApplovinIntegrationRow } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const SELECT = INPUT;
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";

function fmtTs(sec: number): string {
	if (!Number.isFinite(sec) || sec <= 0) return "—";
	try {
		return new Date(sec * 1000).toISOString().replace("T", " ").slice(0, 19) + "Z";
	} catch {
		return "—";
	}
}

function maskKey(k: string): string {
	if (!k) return "";
	if (k.length <= 8) return k[0] + "***" + k[k.length - 1];
	return k.slice(0, 4) + "***" + k.slice(-4);
}

export function ApplovinIntegrationPage() {
	const createMut = useApplovinIntegrationCreate();
	const listQ = useApplovinIntegrationList();

	const [appKey, setAppKey] = useState("");
	const [bundleId, setBundleId] = useState("");
	const [platform, setPlatform] = useState("ios");
	const [sdkVersion, setSdkVersion] = useState("");
	const [enabled, setEnabled] = useState(true);
	const [saved, setSaved] = useState(false);

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		setSaved(false);
		createMut.mutate(
			{
				app_key: appKey.trim(),
				bundle_id: bundleId.trim(),
				platform,
				sdk_version: sdkVersion.trim(),
				enabled,
			},
			{
				onSuccess: () => {
					setSaved(true);
					setAppKey("");
					setBundleId("");
					setSdkVersion("");
				},
			},
		);
	};

	const items: ApplovinIntegrationRow[] = listQ.data?.items ?? [];

	const status = useMemo(() => {
		const total = items.length;
		const enabledCount = items.filter((i) => i.enabled).length;
		const lastSync = items.reduce((acc, i) => Math.max(acc, i.updated_at ?? 0), 0);
		return { total, enabledCount, lastSync };
	}, [items]);

	return (
		<main
			data-testid="applovin-integration-page"
			className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<h1 className="text-3xl font-semibold text-green-700">
					AppLovin Integration
				</h1>
				<p className="text-sm text-zinc-600">
					Register a Mobile Ad Network SDK key to forward install and event postbacks into Trackerity.
				</p>
			</header>

			<section
				aria-label="sync status"
				data-testid="applovin-integration-status"
				className={`${CARD} grid grid-cols-1 gap-4 sm:grid-cols-3`}
			>
				<div className="flex flex-col gap-1">
					<span className="text-xs uppercase tracking-wide text-zinc-500">SDK keys</span>
					<span
						className="text-2xl font-semibold text-zinc-900"
						data-testid="applovin-integration-status-total"
					>
						{status.total}
					</span>
				</div>
				<div className="flex flex-col gap-1">
					<span className="text-xs uppercase tracking-wide text-zinc-500">Active</span>
					<span
						className="text-2xl font-semibold text-green-700"
						data-testid="applovin-integration-status-enabled"
					>
						{status.enabledCount}
					</span>
				</div>
				<div className="flex flex-col gap-1">
					<span className="text-xs uppercase tracking-wide text-zinc-500">Last sync</span>
					<span
						className="text-sm font-medium text-zinc-900"
						data-testid="applovin-integration-status-last-sync"
					>
						{fmtTs(status.lastSync)}
					</span>
				</div>
			</section>

			<form
				onSubmit={handleSubmit}
				className={`${CARD} flex flex-col gap-4`}
				data-testid="applovin-integration-form"
				aria-label="applovin-integration form"
			>
				<h2 className="text-lg font-semibold text-zinc-900">Register SDK key</h2>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="flex flex-col gap-1.5">
						<label htmlFor="applovin-integration-app-key" className={LABEL}>
							SDK key
						</label>
						<input
							id="applovin-integration-app-key"
							type="text"
							required
							maxLength={128}
							className={INPUT}
							value={appKey}
							onChange={(e) => setAppKey(e.currentTarget.value)}
							data-testid="applovin-integration-app-key-input"
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<label htmlFor="applovin-integration-bundle-id" className={LABEL}>
							Bundle ID
						</label>
						<input
							id="applovin-integration-bundle-id"
							type="text"
							required
							maxLength={255}
							placeholder="com.example.app"
							className={INPUT}
							value={bundleId}
							onChange={(e) => setBundleId(e.currentTarget.value)}
							data-testid="applovin-integration-bundle-id-input"
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<label htmlFor="applovin-integration-platform" className={LABEL}>
							Platform
						</label>
						<select
							id="applovin-integration-platform"
							className={SELECT}
							value={platform}
							onChange={(e) => setPlatform(e.currentTarget.value)}
							data-testid="applovin-integration-platform-input"
						>
							<option value="ios">iOS</option>
							<option value="android">Android</option>
						</select>
					</div>

					<div className="flex flex-col gap-1.5">
						<label htmlFor="applovin-integration-sdk-version" className={LABEL}>
							SDK version (optional)
						</label>
						<input
							id="applovin-integration-sdk-version"
							type="text"
							className={INPUT}
							value={sdkVersion}
							onChange={(e) => setSdkVersion(e.currentTarget.value)}
							data-testid="applovin-integration-sdk-version-input"
						/>
					</div>
				</div>

				<label className="flex items-center gap-2 text-sm text-zinc-700">
					<input
						type="checkbox"
						className="h-4 w-4 rounded border-zinc-300 text-green-700 focus:ring-green-600/30"
						checked={enabled}
						onChange={(e) => setEnabled(e.currentTarget.checked)}
						data-testid="applovin-integration-enabled-input"
					/>
					Forward postbacks to Trackerity
				</label>

				<div className="flex items-center gap-3">
					<button
						type="submit"
						className={BTN}
						disabled={createMut.isPending}
						data-testid="applovin-integration-save"
					>
						{createMut.isPending ? "Saving." : "Register key"}
					</button>
					{saved ? (
						<p className="text-sm text-green-700" role="status" data-testid="applovin-integration-saved">
							Saved.
						</p>
					) : null}
					{createMut.error ? (
						<p className="text-sm text-red-700" role="alert" data-testid="applovin-integration-error">
							{createMut.error.message}
						</p>
					) : null}
				</div>
			</form>

			<section
				aria-label="recent installs"
				className={`${CARD} flex flex-col gap-3`}
				data-testid="applovin-integration-feed"
			>
				<h2 className="text-lg font-semibold text-zinc-900">Recent installs</h2>
				{listQ.isPending ? (
					<p className="text-sm text-zinc-600" data-testid="applovin-integration-loading">
						Loading.
					</p>
				) : listQ.error ? (
					<p className="text-sm text-red-700" role="alert" data-testid="applovin-integration-feed-error">
						{listQ.error.message}
					</p>
				) : items.length === 0 ? (
					<p className="text-sm text-zinc-600" data-testid="applovin-integration-empty">
						No SDK keys registered yet. Add one above to start receiving Mobile Ad Network postbacks.
					</p>
				) : (
					<ul
						className="flex flex-col divide-y divide-zinc-200"
						data-testid="applovin-integration-feed-list"
					>
						{items.map((row) => (
							<li
								key={row.id}
								className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between"
								data-testid={`applovin-integration-feed-row-${row.id}`}
							>
								<div className="flex flex-col gap-0.5">
									<span className="font-mono text-sm text-zinc-900">
										{maskKey(row.app_key)}
									</span>
									<span className="text-xs text-zinc-500">
										{row.bundle_id} · {row.platform}
										{row.sdk_version ? ` · sdk ${row.sdk_version}` : ""}
									</span>
								</div>
								<div className="flex items-center gap-3 text-xs">
									<span
										className={
											row.enabled
												? "rounded-full border border-green-700 bg-green-50 px-2 py-0.5 text-green-700"
												: "rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-zinc-600"
										}
									>
										{row.enabled ? "live" : "paused"}
									</span>
									<span className="text-zinc-500">{fmtTs(row.updated_at)}</span>
								</div>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default ApplovinIntegrationPage;
